import * as Sentry from '@sentry/browser';
import { useCookies } from 'react-cookie';

import { AgreementReportStatus, UpdateReportDataType } from '@type-def_iqfu/AgreementReportsType';

import { UserType } from '@constants_common';

const queryString = require('query-string');

const backendBasePath = process.env.NEXT_PUBLIC_DEV_BACKEND_URL;

export const useAgreementReports = () => {
  const [cookies] = useCookies(['expiry', 'access-token', 'client', 'uid', 'user-type']);

  const getAgreementReportsList = async (
    userType: UserType,
    queryParams: {
      company_id?: string;
      offset: number;
      limit: number;
      seller: string;
      settlement_date?: string; //残代金決済日 YYYY-MM
      settlement_date_from?: string; //残代金決済日From YYYY-MM-DD
      settlement_date_to?: string; //残代金決済日To YYYY-MM-DD
      settlement_status?: string[]; //決済状況 settlement_date_unconfirmed, settlement_date_confirmed, completed
      deal_type?: string; //取引タイプ mediation, purchase
      purchase_type?: string; //買取タイプ other_company, in_house
      sort_key?: string; //ソートキー created_at, settlement_date
      sort_order?: string; //ソート順 asc, desc
    },
  ) => {
    if (queryParams.offset < 0) queryParams.offset = 0;
    const query = queryString.stringify(queryParams, { arrayFormat: 'bracket' });

    const url = `${backendBasePath}/${userType}/agreement_reports?${query}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createAgreementReport = async (formData: FormData) => {
    const url = `${backendBasePath}/company/agreement_reports`;
    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: formData,
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getAgreementReportDetail = async (agreement_report_id: string, userType: UserType) => {
    const url = `${backendBasePath}/${userType}/agreement_reports/${agreement_report_id}`;
    let status;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 404) throw new Error();
        status = 'success';
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        status = 'error';
      });
    return { status, result };
  };

  const editAgreementReport = async (agreement_reports: string, submitData: UpdateReportDataType) => {
    const url = `${backendBasePath}/admin/agreement_reports/${agreement_reports}`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateAgreementReport = async (agreement_reports_id: string, formData: FormData) => {
    const url = `${backendBasePath}/company/agreement_reports/${agreement_reports_id}`;
    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: formData,
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteAgreementReport = async (agreement_report_id: string) => {
    const url = `${backendBasePath}/admin/agreement_reports/${agreement_report_id}`;
    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const downloadAgreementReportsFile = async (
    userType: UserType,
    agreement_report_file_id: number,
    file_name: string,
  ) => {
    const url = `${backendBasePath}/${userType}/agreement_report_files/${agreement_report_file_id}/download`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.download = file_name;
        a.href = url;
        a.click();
        a.remove();
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 400);
      })
      .catch((error) => {
        console.error(error);
      });

    return result;
  };

  const previewAgreementReportFile = async (agreement_report_file_id: number) => {
    const url = `${backendBasePath}/admin/agreement_report_files/${agreement_report_file_id}/download`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.blob();
      })
      .then((blob) => {
        return URL.createObjectURL(blob);
      })
      .catch((error) => {
        console.error(error);
      });
    return result;
  };

  const sendSettlementConfirmationChat = async (agreement_report_id: number) => {
    const url = `${backendBasePath}/admin/agreement_reports/${agreement_report_id}/send_settlement_confirmation_chat`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 200) {
          return { success: true };
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { success: false, error };
      });
    return result;
  };

  const updateAgreementReportStatus = async (
    id: string,
    status: AgreementReportStatus,
    settlementDateConfirmed?: boolean,
  ) => {
    const url = `${backendBasePath}/admin/agreement_reports/${id}/update_status`;
    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status, settlement_date_confirmed: settlementDateConfirmed }),
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  return {
    getAgreementReportsList,
    getAgreementReportDetail,
    createAgreementReport,
    editAgreementReport,
    updateAgreementReport,
    deleteAgreementReport,
    downloadAgreementReportsFile,
    previewAgreementReportFile,
    sendSettlementConfirmationChat,
    updateAgreementReportStatus,
  };
};
